import { orderSetStepMemberId } from "@repo/data-store";
import { ENVELOPE_DURATION_INCREMENT_MINUTES, StepPlacement, settle } from "@repo/domain";
import type { PresentedOrderSetDto, PresentedOrderSetOrderDto } from "@/lib/order-set-presentation";
import type { ProcedureOrderOption } from "../components/order-picker-dialog";

/**
 * `step-N`, past the highest the set already carries. The domain raises its own
 * high-water mark from whatever ids the proposal contains, so minting one here
 * cannot collide with what it issues later.
 */
function nextStepId(steps: PresentedOrderSetDto["steps"]): string {
  const high = Math.max(
    0,
    ...steps.map((step) => Number(/^step-([1-9]\d*)$/.exec(orderSetStepMemberId(step.stepId))?.[1] ?? 0)),
  );
  return `step-${high + 1}`;
}

/**
 * The order a proposal carries for a procedure the set does not have yet. Its
 * parameters stay empty: the catalog's declarations arrive with the saved set,
 * and until then the order answers nothing, which is what a draft order is.
 */
function draftOrder(set: PresentedOrderSetDto, option: ProcedureOrderOption, orderId: string) {
  return {
    orderId,
    orderSetId: set.orderSetId,
    title: option.name,
    definition: {
      url: option.definitionUrl,
      version: String(option.version),
      title: option.name,
      kind: option.kind,
      parameters: [],
    },
    parameterValues: [],
    status: "draft",
    charted: false,
    notPerformed: false,
    observations: [],
    chartingObservations: [],
  } as unknown as PresentedOrderSetOrderDto;
}

/**
 * Adds a procedure to a booked set's PROPOSAL — the shape it shows until Submit
 * re-cuts the booking. `addOrder` does this server-side for an unbooked set; a
 * booked one refuses that write. Joining a step preserves its authored duration;
 * creating a new step reserves one scheduling increment.
 *
 * A null `stepId` means there is no step to join, so the procedure becomes a
 * step of its own at the end — the reading `addOrder` gives an omitted
 * `concurrentWithStep`.
 *
 * The proposal owner reconciles the envelope after each edit, including this
 * addition, before displaying or checking the resulting schedule.
 */
export function addProcedureToOrderSet(
  set: PresentedOrderSetDto,
  stepId: string | null,
  option: ProcedureOrderOption,
  orderId: string,
): PresentedOrderSetDto {
  const index = stepId === null ? -1 : set.steps.findIndex((step) => step.stepId === stepId);
  // A named step that is not here is a stale selection, not a request to append.
  if (stepId !== null && index < 0) return set;
  const order = draftOrder(set, option, orderId);
  if (index < 0) {
    const end = Math.max(0, ...set.steps.map((step) => new StepPlacement(step.placement).endMinutes));
    return {
      ...set,
      steps: [
        ...set.steps,
        {
          stepId: nextStepId(set.steps),
          placement: { offsetMinutes: end, durationMinutes: ENVELOPE_DURATION_INCREMENT_MINUTES },
          orders: [order],
        },
      ],
      orders: [...set.orders, order],
    };
  }
  const placed = set.steps.map((step, position) => {
    const placement = new StepPlacement(step.placement);
    if (position !== index) return { ...step, placement };
    const orders = [...step.orders, order];
    return { ...step, orders, placement };
  });
  return {
    ...set,
    steps: settle(placed, index).map(({ placement, ...step }) => ({
      ...step,
      placement: { offsetMinutes: placement.offsetMinutes, durationMinutes: placement.durationMinutes },
    })),
    orders: [...set.orders, order],
  };
}
